import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from './SocketContext';
import { usePractice } from './PracticeContext';
import axios from 'axios';
import { getDeviceId } from '../lib/deviceId';
import { playSoundForAlert, initializeAudio } from '../lib/sounds';

const AlertContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const { socket } = useSocket();
  const { practice } = usePractice();
  const previousAlertIds = useRef(new Set());
  const audioInitialized = useRef(false);
  
  // Use filtering based on practice setting (defaults to true if not set)
  const useFiltering = practice?.alert_filtering_enabled !== false;

  // Initialize audio on first user interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!audioInitialized.current) {
        initializeAudio();
        audioInitialized.current = true;
      }
    };
    
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Play sound for new alerts
  const checkAndPlaySound = useCallback((newAlerts) => {
    if (!practice) return;
    
    const soundSettings = {
      sounds_enabled: practice.sounds_enabled !== false,
      sound_volume: practice.sound_volume ?? 0.7,
      sound_assignments: practice.sound_assignments || {}
    };
    
    if (!soundSettings.sounds_enabled) return;
    
    // Find truly new alerts (not seen before)
    newAlerts.forEach(alert => {
      if (!previousAlertIds.current.has(alert.id)) {
        // This is a new alert - play sound
        let alertType = alert.alert_type;
        
        // Map alert types to sound assignment keys
        if (alertType === 'assistant_help') alertType = 'help_needed';
        if (alertType === 'custom_button') {
          // Get custom button index from metadata
          const buttonIndex = alert.metadata?.button_index || 1;
          alertType = `custom_button_${buttonIndex}`;
        }
        
        playSoundForAlert(alertType, soundSettings);
      }
    });
    
    // Update the set of known alert IDs
    const currentIds = new Set(newAlerts.map(a => a.id));
    previousAlertIds.current = currentIds;
  }, [practice]);

  const fetchAlerts = useCallback(async () => {
    try {
      let response;
      if (useFiltering) {
        // Use filtered endpoint with device_id for role-based filtering
        const deviceId = getDeviceId();
        response = await axios.get(`${API}/alerts/filtered?device_id=${deviceId}`);
      } else {
        // Fallback to unfiltered alerts (for admin override or debugging)
        response = await axios.get(`${API}/alerts?status=active`);
      }
      
      const newAlerts = response.data;
      checkAndPlaySound(newAlerts);
      setAlerts(newAlerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      // Fallback to unfiltered if filtered endpoint fails
      try {
        const response = await axios.get(`${API}/alerts?status=active`);
        checkAndPlaySound(response.data);
        setAlerts(response.data);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  }, [useFiltering, checkAndPlaySound]);

  useEffect(() => {
    fetchAlerts();
    if (socket) {
      // Socket connected — rely on events, use 30s fallback only for safety
      const handleAlertEvent = () => fetchAlerts();
      socket.on('new_alert', handleAlertEvent);
      socket.on('alert_updated', handleAlertEvent);
      socket.on('alert_created', handleAlertEvent);
      socket.on('alert_read', handleAlertEvent);
      socket.on('alert_advanced', handleAlertEvent);
      socket.on('operatory_updated', handleAlertEvent);
      const fallback = setInterval(fetchAlerts, 30000);
      return () => {
        socket.off('new_alert', handleAlertEvent);
        socket.off('alert_updated', handleAlertEvent);
        socket.off('alert_created', handleAlertEvent);
        socket.off('alert_read', handleAlertEvent);
        socket.off('alert_advanced', handleAlertEvent);
        socket.off('operatory_updated', handleAlertEvent);
        clearInterval(fallback);
      };
    }
    // No socket — poll every 5s as before
    const interval = setInterval(fetchAlerts, 5000);
    return () => clearInterval(interval);
  }, [fetchAlerts, socket]);

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}`, { status: 'acknowledged' });
      fetchAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const completeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}`, { status: 'completed' });
      fetchAlerts();
    } catch (error) {
      console.error('Failed to complete alert:', error);
    }
  };

  return (
    <AlertContext.Provider value={{ alerts, acknowledgeAlert, completeAlert, fetchAlerts, useFiltering }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within AlertProvider');
  }
  return context;
};